import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, KeyRound, Plus, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btnBlue, btnRed, card, dayLabel, Field, inp, ROLE, Tag } from "@/components/club/ui";

const Redeem = ({ onDone }) => {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const go = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/club/redeem", { code });
      toast.success("¡Clave aceptada! Ya tienes acceso.");
      onDone();
      nav(data.scope === "competition" ? `/club/concurso/${data.target_id}` : `/club/${data.club_id}`);
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={go} className="bg-[#0B1D3A] text-white rounded-2xl p-6 min-w-0" data-testid="redeem-form">
      <p className="flex items-center gap-2 font-display font-black text-2xl"><KeyRound className="text-[#93C5FD]" />¿Tienes una clave de acceso?</p>
      <p className="text-blue-100 text-sm mt-1">Te la da tu club para entrar en sus clases privadas, grupos y concursos cerrados.</p>
      <div className="flex gap-2 mt-4">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Ej. 2KBPHQ4Y" maxLength={20} className="flex-1 min-w-0 rounded-full px-5 font-mono tracking-widest uppercase" data-testid="redeem-input" />
        <button className={btnRed} data-testid="redeem-submit">Entrar</button>
      </div>
    </form>
  );
};

const CreateClub = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", city: "", description: "" });
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/club/clubs", f); toast.success("Club creado. Eres su administrador."); onDone(); nav(`/club/${data.id}`); } catch (e2) { toast.error(errMsg(e2)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className={btnBlue} data-testid="create-club-open"><Plus size={18} />Dar de alta mi club o escuela</button>;
  return (
    <form onSubmit={submit} className={`${card} grid sm:grid-cols-2 gap-4`} data-testid="create-club-form">
      <Field label="Nombre del club"><input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="club-name" /></Field>
      <Field label="Ciudad"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inp} data-testid="club-city" /></Field>
      <Field label="Descripción"><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full mt-1 rounded-lg p-3" data-testid="club-description" /></Field>
      <div className="flex items-end gap-2"><button className={btnRed} data-testid="club-submit">Crear club</button><button type="button" onClick={() => setOpen(false)} className="font-bold text-slate-500 px-3">Cancelar</button></div>
    </form>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <section className="mt-10"><h2 className="flex items-center gap-2 font-display font-black uppercase text-2xl text-[#0B1D3A] mb-4"><Icon className="text-[#3D88B8]" />{title}</h2>{children}</section>
);

export default function ClubHub() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [comps, setComps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [entries, setEntries] = useState([]);
  const load = () => {
    api.get("/club/clubs").then((r) => setClubs(r.data));
    api.get("/club/competitions").then((r) => setComps(r.data));
    if (user) {
      api.get("/club/mine").then((r) => setMine(r.data));
      api.get("/club/me/bookings").then((r) => setBookings(r.data));
      api.get("/club/me/entries").then((r) => setEntries(r.data));
    }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const mineIds = new Set(mine.map((c) => c.id));

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="club-hub">
      <p className="text-[#B23A38] font-bold uppercase tracking-widest text-sm">FAUNNY Club</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-[#0B1D3A] leading-none mt-2">Clases y concursos<br />de tu club hípico</h1>
      <p className="text-slate-600 mt-4 max-w-2xl">Reserva tus clases, inscríbete en concursos, recibe avisos en el móvil y consulta los resultados.</p>
      {user === false ? (
        <div className={`${card} mt-8 flex flex-wrap items-center gap-4`} data-testid="club-login-cta">
          <p className="flex-1 text-lg">Para usar FAUNNY Club necesitas una cuenta gratuita.</p>
          <Link to="/registro?next=/club" className={btnRed} data-testid="club-register">Crear cuenta</Link>
          <Link to="/login?next=/club" className="font-bold text-[#3D88B8]" data-testid="club-login">Ya tengo cuenta</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-8 [&>*]:min-w-0"><Redeem onDone={load} /><div className="flex items-center"><CreateClub onDone={load} /></div></div>
      )}

      {!!mine.length && <Section title="Mis clubes" icon={CalendarDays}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{mine.map((c) => (
          <Link key={c.id} to={`/club/${c.id}`} className={`${card} hover:border-[#3D88B8] transition-colors`} data-testid={`my-club-${c.id}`}>
            <p className="font-bold text-lg text-[#0B1D3A]">{c.name}</p><p className="text-sm text-slate-500">{c.city}</p><div className="mt-2"><Tag>{ROLE[c.role]}</Tag></div>
          </Link>))}</div>
      </Section>}

      {!!bookings.length && <Section title="Mis próximas clases" icon={CalendarDays}>
        <ul className="space-y-2">{bookings.map((b) => (
          <li key={b.id} className={`${card} flex flex-wrap items-center gap-3`} data-testid={`my-booking-${b.id}`}>
            <span className="font-display font-black text-xl w-28">{dayLabel(b.date)} {b.time}</span>
            <span className="flex-1"><b>{b.title}</b> · {b.club_name}{b.my_note && <span className="block text-sm text-slate-500">Nota: {b.my_note}</span>}</span>
            {b.status === "cancelled" ? <Tag tone="red">Cancelada</Tag> : <Tag tone={b.mine === "booked" ? "green" : "gray"}>{b.mine === "booked" ? "Reservada" : "En espera"}</Tag>}
          </li>))}</ul>
      </Section>}

      {!!entries.length && <Section title="Mi historial de concursos" icon={Trophy}>
        <ul className="space-y-2">{entries.map((e) => (
          <li key={e.id} className={`${card} flex flex-wrap items-center gap-3`} data-testid={`my-entry-${e.id}`}>
            <Link to={`/club/concurso/${e.competition_id}`} className="flex-1"><b>{e.competition.name}</b> · {e.competition.date} · {e.horse_name} · {e.category}</Link>
            {e.result ? <Tag tone="green">{e.result.position ? `Puesto ${e.result.position}` : e.result.status}</Tag> : <Tag tone={e.status === "confirmed" ? "blue" : "gray"}>{{ confirmed: "Inscrito", pending_payment: "Pendiente de pago", cancelled: "Anulada" }[e.status]}</Tag>}
          </li>))}</ul>
      </Section>}

      <Section title="Concursos abiertos" icon={Trophy}>
        {!comps.length ? <p className="text-slate-500">Todavía no hay concursos publicados.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{comps.map((c) => (
            <Link key={c.id} to={`/club/concurso/${c.id}`} className={`${card} hover:border-[#B23A38] transition-colors`} data-testid={`comp-card-${c.id}`}>
              <p className="text-sm font-bold text-[#B23A38]">{dayLabel(c.date)} · {c.discipline}</p>
              <p className="font-bold text-lg text-[#0B1D3A] mt-1">{c.name}</p>
              <p className="text-sm text-slate-500">{c.club_name}{c.place ? ` · ${c.place}` : ""}</p>
              <p className="text-sm mt-2">{c.fee > 0 ? `${c.fee.toFixed(2).replace(".", ",")} €` : "Gratis"} · hasta {dayLabel(c.deadline)}</p>
            </Link>))}</div>
        )}
      </Section>

      <Section title="Clubes en FAUNNY" icon={CalendarDays}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{clubs.map((c) => (
          <div key={c.id} className={card} data-testid={`club-card-${c.id}`}>
            <Link to={`/club/${c.id}`} className="font-bold text-lg text-[#0B1D3A] hover:text-[#3D88B8]">{c.name}</Link>
            <p className="text-sm text-slate-500">{c.city}</p>
            {user && !mineIds.has(c.id) && <button onClick={async () => { await api.post(`/club/clubs/${c.id}/follow`); toast.success("Ahora sigues a este club"); load(); }} className="mt-3 text-sm font-bold text-[#3D88B8]" data-testid={`follow-${c.id}`}>Seguir club</button>}
          </div>))}</div>
      </Section>
    </div>
  );
}
